import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZapIcon, BotIcon, CpuIcon, PuzzleIcon, BookOpenIcon, PlayIcon, Trash2Icon, FlaskConicalIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useSessionStore } from '../stores/sessionStore';
import { useModelStore } from '../stores/modelStore';
import { useUiStore } from '../stores/uiStore';
import { Session } from '../types';
import { PageContainer, PageHeader, EmptyState } from '../components/common/page';
import { Card, Badge, Button } from '../components/common/ui';
import { formatRelative } from '../utils/format';
type Tab = 'temporary' | 'agent';
export function HistorySessionsPage() {
  useScreenInit();
  const navigate = useNavigate();
  const sessions = useSessionStore(s => s.sessions);
  const deleteSession = useSessionStore(s => s.deleteSession);
  const models = useModelStore(s => s.models);
  const pushToast = useUiStore(s => s.pushToast);
  const [tab, setTab] = useState<Tab>('agent');
  const modelName = (id: string) => models.find(m => m.modelId === id)?.name || '未指定';
  const temporary = sessions.filter(s => s.sessionType === 'temporary');
  const agentSessions = sessions.filter(s => s.sessionType === 'agent');
  const groupedAgent = useMemo(() => {
    const map: Record<string, Session[]> = {};
    agentSessions.forEach(s => {
      const key = s.agentName || '未知 Agent';
      (map[key] = map[key] || []).push(s);
    });
    return map;
  }, [agentSessions]);
  const handleDelete = (s: Session) => {
    if (confirm('确认删除该会话？此操作不可恢复。')) {
      deleteSession(s.sessionId);
      pushToast('success', '会话已删除');
    }
  };
  const SessionRow = ({
    s
  }: {
    s: Session;
  }) => <Card className="p-4" data-id="element-538">
      <div className="flex flex-wrap items-center gap-2" data-id="element-539">
        <span className="font-medium text-slate-100" data-id="element-540">{s.title}</span>
        {s.isTest && <Badge tone="amber" data-id="element-541">
            <FlaskConicalIcon className="h-3 w-3" data-id="element-542" /> 测试
          </Badge>}
        <Badge tone="indigo" data-id="element-543">
          <CpuIcon className="h-3 w-3" data-id="element-544" /> {modelName(s.modelId)}
        </Badge>
        <span className="ml-auto text-xs text-slate-500" data-id="element-545">
          {formatRelative(s.updatedAt)}
        </span>
      </div>
      <p className="mt-1.5 line-clamp-1 text-sm text-slate-400" data-id="element-546">
        {s.lastMessagePreview || '（暂无消息）'}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500" data-id="element-547">
        <span className="inline-flex items-center gap-1" data-id="element-548">
          <PuzzleIcon className="h-3 w-3" data-id="element-549" /> {s.skillIds.length} Skills
        </span>
        <span className="inline-flex items-center gap-1" data-id="element-550">
          <BookOpenIcon className="h-3 w-3" data-id="element-551" /> {s.knowledgeBaseIds.length}{' '}
          知识库
        </span>
      </div>
      <div className="mt-3 flex gap-2 border-t border-slate-800 pt-3" data-id="element-552">
        <Button size="sm" variant="outline" onClick={() => navigate(`/history/${s.sessionId}`)} data-id="element-553">
          <PlayIcon className="h-3.5 w-3.5" data-id="element-554" /> 恢复
        </Button>
        <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} className="text-red-400 hover:text-red-300" aria-label="删除会话" data-id="element-555">
          <Trash2Icon className="h-3.5 w-3.5" data-id="element-556" />
        </Button>
      </div>
    </Card>;
  return <PageContainer data-id="element-557">
      <PageHeader title="历史会话" description="查看与管理临时会话和 Agent 会话（仅显示当前用户的会话）" data-id="element-558" />

      {/* Tabs */}
      <div className="mb-5 inline-flex rounded-lg border border-slate-800 bg-slate-900/60 p-1" data-id="element-559">
        <button onClick={() => setTab('agent')} className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === 'agent' ? 'bg-indigo-600/20 text-indigo-300' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-560">
          <BotIcon className="h-4 w-4" data-id="element-561" /> Agent 会话 ({agentSessions.length})
        </button>
        <button onClick={() => setTab('temporary')} className={`inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === 'temporary' ? 'bg-indigo-600/20 text-indigo-300' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-562">
          <ZapIcon className="h-4 w-4" data-id="element-563" /> 临时会话 ({temporary.length})
        </button>
      </div>

      {tab === 'agent' && (agentSessions.length === 0 ? <EmptyState icon={BotIcon} title="暂无 Agent 会话" description="在 Agent 中心或对话页选择 Agent 并发送消息后，会话将出现在这里" data-id="element-564" /> : <div className="space-y-6" data-id="element-565">
            {Object.entries(groupedAgent).map(([agentName, list]) => <div key={agentName} data-id="element-566">
                <div className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-300" data-id="element-567">
                  <BotIcon className="h-4 w-4 text-indigo-400" data-id="element-568" />
                  {agentName}
                  <span className="text-xs text-slate-500" data-id="element-569">
                    ({list.length})
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-570">
                  {list.map(s => <SessionRow key={s.sessionId} s={s} data-id="element-571" />)}
                </div>
              </div>)}
          </div>)}

      {tab === 'temporary' && (temporary.length === 0 ? <EmptyState icon={ZapIcon} title="暂无临时会话" description="在对话页点击「新建临时会话」并发送消息后，会话将出现在这里" data-id="element-572" /> : <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-573">
            {temporary.map(s => <SessionRow key={s.sessionId} s={s} data-id="element-574" />)}
          </div>)}
    </PageContainer>;
}